// Win/draw/loss counting, the building block of every card that splits games
// by result: the whole period, or keyed (by colour, rating band, ending).
import { type Accumulator } from '@chess/api-model';
import type { ResultCounts } from './types.ts';
import { type GameFacts } from '@chess/api-model';

export const emptyResults = (): ResultCounts => ({ win: 0, draw: 0, loss: 0 });

/** The games a result count holds. */
export const totalOf = (r: ResultCounts): number => r.win + r.draw + r.loss;

/** The period's results. */
export class ResultsAccumulator implements Accumulator<ResultCounts> {
  private readonly counts = emptyResults();

  add(game: GameFacts): void {
    this.counts[game.outcome] += 1;
  }

  result(): ResultCounts {
    return { ...this.counts };
  }
}

/**
 * Results by a key read from each game; games without a key are skipped.
 * `keys` lists the keys reported even when no game has them, in order.
 */
export class KeyedResultsAccumulator<K extends string> implements Accumulator<Record<K, ResultCounts>> {
  private readonly counts = new Map<K, ResultCounts>();
  private readonly keyOf: (game: GameFacts) => K | undefined;

  constructor(keys: readonly K[], keyOf: (game: GameFacts) => K | undefined) {
    this.keyOf = keyOf;
    for (const k of keys) this.counts.set(k, emptyResults());
  }

  add(game: GameFacts): void {
    const key = this.keyOf(game);
    if (key === undefined) return;
    let counts = this.counts.get(key);
    if (!counts) this.counts.set(key, (counts = emptyResults()));
    counts[game.outcome] += 1;
  }

  result(): Record<K, ResultCounts> {
    const out = {} as Record<K, ResultCounts>;
    for (const [k, r] of this.counts) out[k] = { ...r };
    return out;
  }
}
